import { useState, useEffect } from "react";
import { supabase } from "@/shared/integrations/supabase/client";
import { useToast } from "@/shared/hooks/use-toast";
import { fetchAuthorizedProperties, fetchPropertiesByOrgId } from "@/features/projects/services/projectsApi";
import { isAdminUser } from "@/shared/lib/adminUids";
import { firebaseAuth } from "@/shared/integrations/firebase/client";

export interface AssetsSummary {
  totalAssets: number;
  operationalCount: number;
  maintenanceCount: number;
  propertiesCount: number;
  assetsByType: Record<string, number>;
  assetsByStatus: Record<string, number>;
}

export function useClientAssetsSummary(
  clientId: string,
  statusFilter?: string,
  categoryFilter?: string,
  propertyFilter?: string,
  searchTerm?: string,
) {
  const [summary, setSummary] = useState<AssetsSummary>({
    totalAssets: 0,
    operationalCount: 0,
    maintenanceCount: 0,
    propertiesCount: 0,
    assetsByType: {},
    assetsByStatus: {},
  });
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  // MVP2-282: Keep previous summary to prevent flicker during search
  const [previousSummary, setPreviousSummary] = useState<AssetsSummary | null>(null);

  useEffect(() => {
    const fetchSummary = async () => {
      if (!clientId) return;
      
      try {
        // MVP2-282: Save current summary before loading new one (optimistic UI)
        if (summary.totalAssets > 0) {
          setPreviousSummary(summary);
        }
        
        setLoading(true);
        
        // Get this client's properties (admins: selected client by
        // organization_id; client users: authorized set from the endpoint).
        const allProperties = isAdminUser(firebaseAuth?.currentUser?.uid)
          ? await fetchPropertiesByOrgId<{ id: string }>(clientId, "id")
          : await fetchAuthorizedProperties<{ id: string }>("id");
        let propertyIds = allProperties.map(p => p.id);

        // Apply property filter
        if (propertyFilter) {
          propertyIds = [propertyFilter];
        }

        if (propertyIds.length === 0) {
          setSummary({
            totalAssets: 0,
            operationalCount: 0,
            maintenanceCount: 0,
            propertiesCount: 0,
            assetsByType: {},
            assetsByStatus: {},
          });
          return;
        }

        // Build base query for counting total assets
        let countQuery = supabase
          .from('property_assets')
          .select('id', { count: 'exact', head: true })
          .in('property_id', propertyIds);

        // Apply filters to count query
        if (statusFilter) {
          countQuery = countQuery.eq('condition_status', statusFilter);
        }
        if (categoryFilter) {
          countQuery = countQuery.eq('category_id', categoryFilter);
        }

        if (searchTerm) {
          const search = `%${searchTerm}%`;
          countQuery = countQuery.or(`name.ilike.${search},manufacturer.ilike.${search},model_number.ilike.${search},model_name.ilike.${search},serial_number.ilike.${search}`);
        }

        const { count: totalCount, error: countError } = await countQuery;
        if (countError) throw countError;

        // Fetch ALL status and type data in batches to avoid 1000 row limit
        let allAggregatedData: any[] = [];
        let offset = 0;
        const BATCH_SIZE = 1000;
        let hasMore = true;

        while (hasMore) {
          let batchQuery = supabase
            .from('property_assets')
            .select('status, category_id, asset_categories(name), property_id')
            .in('property_id', propertyIds)
            .range(offset, offset + BATCH_SIZE - 1);

          // Apply same filters
          if (statusFilter) {
            batchQuery = batchQuery.eq('condition_status', statusFilter);
          }
          if (categoryFilter) {
            batchQuery = batchQuery.eq('category_id', categoryFilter);
          }

          if (searchTerm) {
            const search = `%${searchTerm}%`;
            batchQuery = batchQuery.or(`name.ilike.${search},manufacturer.ilike.${search},model_number.ilike.${search},model_name.ilike.${search},serial_number.ilike.${search}`);
          }

          const { data, error } = await batchQuery;
          if (error) throw error;

          if (data && data.length > 0) {
            allAggregatedData = [...allAggregatedData, ...data];
            offset += BATCH_SIZE;
            hasMore = data.length === BATCH_SIZE;
          } else {
            hasMore = false;
          }
        }

        // Aggregate all data in JavaScript for accurate counts
        const statusCounts: Record<string, number> = {};
        const typeCounts: Record<string, number> = {};
        const uniquePropertyIds = new Set<string>();
        let operational = 0;
        let maintenance = 0;
        
        allAggregatedData.forEach(item => {
          // Count status
          const status = item.status || 'unknown';
          statusCounts[status] = (statusCounts[status] || 0) + 1;
          
          // Check for operational/active status (case-insensitive)
          const statusLower = status.toLowerCase();
          if (['operational', 'active', 'good', 'excellent'].includes(statusLower)) {
            operational++;
          } else if (['maintenance_required', 'needs_maintenance', 'poor', 'critical', 'fair'].includes(statusLower)) {
            maintenance++;
          }

          // Count type (resolved from the asset_categories join)
          const type = (item.asset_categories as any)?.name;
          if (type) {
            typeCounts[type] = (typeCounts[type] || 0) + 1;
          }

          // Track unique properties
          if (item.property_id) {
            uniquePropertyIds.add(item.property_id);
          }
        });

        setSummary({
          totalAssets: totalCount || 0,
          operationalCount: operational,
          maintenanceCount: maintenance,
          propertiesCount: uniquePropertyIds.size,
          assetsByType: typeCounts,
          assetsByStatus: statusCounts,
        });
      } catch (error) {
        console.error('Error fetching asset summary:', error);
        toast({
          title: "Error",
          description: "Failed to load asset summary",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchSummary();
  }, [clientId, statusFilter, categoryFilter, propertyFilter, searchTerm, toast]);

  // MVP2-282: Use previous summary during loading to prevent flicker
  const displaySummary = loading && previousSummary ? previousSummary : summary;
  const isRefreshing = loading && previousSummary !== null;

  return { summary: displaySummary, loading, isRefreshing };
}
